import { default as _log } from '@dw/loglevel';
import EventEmitter from "@dreamworld/event-emitter";
import { uuid } from "@dreamworld/uuid-base62";
import LocalDatabase from "./local-database.js";
import RemoteDatabase from "./remote-database.js";
import WriteDocsResponse from "./write-docs-response.js";
import QueryById from "./query-by-id.js";
import QueryByIds from "./query-by-ids.js";
import QueryFind from "./query-find.js";
import QueryFindOne from "./query-find-one.js";
import QueryView from "./query-view.js";

//lodash
import forEach from "lodash-es/forEach";

export default class Database extends EventEmitter {
  constructor(dbName, couchdb, dispatch) {
    super();
    this.couchdb = couchdb;
    this.dispatch = dispatch;
    this.dbName = dbName;
    this.remoteDb = new RemoteDatabase(dbName, couchdb);
    this.localDb = new LocalDatabase(dbName, couchdb);

    //Holds all the live queries. key = queryId, value = Query.
    //When query is cancelled, it's removed from here.
    this.queries = {};

    // logger prefix
    this.logPrefix = `dreamDB.couchDB.${this.couchdb.name}.db.${this.dbName}`;

    // Set logger
    this.log = _log.getLogger(`dreamdb.database.${this.couchdb.name}.${this.dbName}`);
    this._removeQueryReference = this._removeQueryReference.bind(this);
  }

  /**
   * This method is used for disconnect a connection of current database
   * It will cancel all queries for this database which are live.
   */
  disconnect() {
    //It will cancel all live queries for this database.
    forEach(this.queries, (queryRef) => {
      queryRef.cancel();
    });

    this.log.info(`${this.logPrefix}.disconnect(): All live queries are cancelled.`)

    //It will close connection of local database
    this.localDb.disconnect();
    this.log.info(`${this.logPrefix}.disconnect(): local database is disconnected successfully.`)
  }

  /**
   * This method is used to destory local database.
   */
  async destroy() {
    try {
      //Removes local pouchdb data
      let res = await this.localDb.destroy();
      this.log.info(`${this.logPrefix}.destroy(): local database destroyed successfully.`, res);
    } catch (err) {
      this.log.error(`${this.logPrefix}.destroy(): database destroy action failed`, err);
    }
  }

  /**
   * Stops listening for the live changes for the given queryIdPrefix.
   * It calls queries's `cancel` method which mathches prefix
   * 
   * @param {String} queryIdPrefix - prefix of the queryId
   */
  cancelAll(queryIdPrefix) {
    if (!queryIdPrefix) {
      this.log.error(`${this.logPrefix}.cancelAll(): queryIdPrefix is required.`);
      return;
    }

    if (typeof queryIdPrefix !== 'string') {
      this.log.error(`${this.logPrefix}.cancelAll(): queryIdPrefix should be type of String.`);
      return;
    }

    forEach(this.queries, (queryRef, queryId) => {
      if (queryId.startsWith(queryIdPrefix)) {
        queryRef.cancel();
      }
    });

    this.log.info(`${this.logPrefix}.cancelAll(): All queries from database=${this.dbName} has been cancelled which is startsWith=${queryIdPrefix}.`);
  }

  /**
   * It's query's `cancel` event handler.
   * @param {String} queryId - id of the query
   */
  _removeQueryReference(queryId) {
    delete this.queries[queryId];
  }

  /**
   * This method is used to get document by id.
   * 
   * It creates query instance of `QueryById` then store it into queries hash.
   * It handles `cancel` event.
   * 
   * @param {*} can be object or array
   *  - If `Object` it should have `docId`, queryId is optional. Automatically generated if itn't provided. e.g. {docId: '23423-4234234'}
   *  - If `String` Which is id of the document
   * .
   * @returns {Object} Returns `QueryById` class instance 
   */
  getById(param) {

    if (typeof param !== 'object' && typeof param !== 'string') {
      this.log.error(`${this.logPrefix}.getById():: Argument should be object or string.`);
      return;
    }

    let queryData = {};

    if (typeof param === 'object') {
      queryData = param;
    }

    if (typeof param === 'string') {
      queryData['docId'] = param;
    }

    let { queryId, docId, localOnly, once } = queryData;

    if (!docId) {
      this.log.error(`${this.logPrefix}.getById():: docId is missing. Please provide it to read a document`);
      return;
    }

    if (typeof docId !== 'string') {
      this.log.error(`${this.logPrefix}.getById():: docId should be type of String. Use getByIds() to read multiple documents`);
      return;
    }

    if (!queryId) {
      queryId = uuid();
    }

    //Returns same instance if query is already available with same queryId
    if (this.queries[queryId]) {
      this.log.error(`${this.logPrefix}.getById(): query is already live with queryId=${queryId}. Use update() to update query criteria.`,);
      return this.queries[queryId];
    }

    let query = new QueryById({ couchdb: this.couchdb, database: this, queryId, docId, localOnly, once, dispatch: this.dispatch });

    if(once) {
      return query;
    }

    this.queries[queryId] = query;

    //Bind `cancel` event on query instance.
    query.on('cancel', this._removeQueryReference);
    return query;
  }

  /**
   * This method is used to get multiple documents using multiple docIds
   * 
   * It creates query instance of `QueryByIds` then store it into queries hash.
   * It handles `cancel` event.
   * 
   * @param {*} can be object or array
   *  - If `Object` it should have `docIds` key which is array of docIds to be fetch. `queryId` is optional
   *  - If `Array` it should be array of `docIds`
   * @returns {Object} Returns `QueryByIds` class instance 
   */
  getByIds(param) {

    if (typeof param !== 'object') {
      this.log.error(`${this.logPrefix}.getByIds():: Argument should be object or array.`);
      return;
    }

    let queryData = {};

    if (Array.isArray(param)) {
      queryData['docIds'] = param;
    }

    if (!Array.isArray(param)) {
      queryData = param;
    }

    let { queryId, docIds, localOnly, once } = queryData;

    if (!docIds || !Array.isArray(docIds)) {
      this.log.error(`${this.logPrefix}.getByIds():: docIds are required and it should be array of ids.`);
      return;
    }

    if (!queryId) {
      queryId = uuid();
    }

    let isAllString = docIds.every((id) => {
      return typeof id === 'string';
    });

    if (!isAllString) {
      this.log.warn(`${this.logPrefix}.getByIds(): docIds should be array of string, Here all ids are not type of string`);
    }

    if (!docIds.length) {
      this.log.error(`${this.logPrefix}.getByIds(): atleast one docId is required to perform query.`);
      return;
    }

    //Returns same instance if query is already available with same queryId
    if (this.queries[queryId]) {
      this.log.error(`${this.logPrefix}.getByIds(): query is already live with queryId=${queryId}. Use update() to update query criteria.`);
      return this.queries[queryId];
    }

    let query = new QueryByIds({ couchdb: this.couchdb, database: this, queryId, docIds, localOnly, once, dispatch: this.dispatch });

    if(once) {
      return query;
    }

    this.queries[queryId] = query;

    //Bind `cancel` event on query instance.
    query.on('cancel', this._removeQueryReference);
    return query;
  }

  /**
   * This method is used find list of documents which are matched with the given criteria.
   * 
   * It creates query instance of `QueryFind` then store it into queries hash.
   * It Handles `cancel` event of the query
   * 
   * @param {Object} - It should contain `selector` and `useIndex` fields. `limit`, `skip`, `sort`, queryId are optional. If queryId not available it will generate automatically.
   * @requires {Object} Returns `QueryFind` class instance 
   */
  find({ queryId, selector, useIndex, limit, skip, sort, localOnly, once }) {

    if (!selector || !useIndex) {
      this.log.error(`${this.logPrefix}.find():: selector and useIndex are required.`);
      return;
    }

    if (!queryId) {
      queryId = uuid();
    }

    //Returns same instance if query is already available with same queryId
    if (this.queries[queryId]) {
      this.log.error(`${this.logPrefix}.find(): query is already live with queryId=${queryId}. Use update() to update query criteria.`,);
      return this.queries[queryId];
    }

    let query = new QueryFind({ couchdb: this.couchdb, database: this, queryId, dispatch: this.dispatch, selector, useIndex, limit, skip, sort, localOnly, once });

    if(once) {
      return query;
    }

    this.queries[queryId] = query;

    //Bind `cancel` event on query instance.
    query.on('cancel', this._removeQueryReference);
    return query;
  }

  /**
   * This method is used to find one document which matches selector.
   * Logs error if it's found more than one document.
   * 
   * It creates query instance of `QueryFindOne` then store it into queries hash.
   * It Handles `cancel` event of the query
   * 
   * @param {Object} - It should contain `selector` and `useIndex` fields. queryId is optional. If queryId not available it will generate automatically.
   * @requires {Object} Returns `QueryFindOne` class instance 
   */
  findOne({ queryId, selector, useIndex, localOnly, once }) {

    if (!selector || !useIndex) {
      this.log.error(`${this.logPrefix}.findOne():: selector and useIndex are required.`);
      return;
    }

    if (!queryId) {
      queryId = uuid();
    }

    //Returns same instance if query is already available with same queryId
    if (this.queries[queryId]) {
      this.log.error(`${this.logPrefix}.findOne(): query is already live with queryId=${queryId}. Use update() to update query criteria.`,);
      return this.queries[queryId];
    }

    let query = new QueryFindOne({ couchdb: this.couchdb, database: this, queryId, dispatch: this.dispatch, selector, useIndex, localOnly, once });

    if(once) {
      return query;
    }

    this.queries[queryId] = query;

    //Bind `cancel` event on query instance.
    query.on('cancel', this._removeQueryReference);
    return query;
  }

  /**
   * This method is used to perform view query using map/reduce function.
   * 
   * It creates query instance of `QueryView` then store it into queries hash.
   * It Handles `cancel` event of the query
   * 
   * @param {String} view - view name
   * @param {Object} options - queryId is optional. If queryId not available it will generate automatically.
   * @requires {Object} Returns `QueryView` class instance 
   */
  query(view, options = {}) {

    if (!view) {
      this.log.error(`${this.logPrefix}.query():: view name is required.`);
      return;
    }

    if (typeof view !== 'string') {
      this.log.error(`${this.logPrefix}.query():: view name should be string.`);
      return;
    }

    if (view.startsWith('_design/')) {
      this.log.error(`${this.logPrefix}.query():: view name is invalid`);
      return;
    }

    if (typeof options !== 'object') {
      this.log.error(`${this.logPrefix}.query():: options should be object.`);
      return;
    }


    let { queryId, once } = options;

    if (!queryId) {
      queryId = uuid();
    }

    //Returns same instance if query is already available with same queryId
    if (this.queries[queryId]) {
      this.log.error(`${this.logPrefix}.query(): query is already live with queryId=${queryId}. Use update() to update query criteria.`,);
      return this.queries[queryId];
    }

    let params = { ...options, couchdb: this.couchdb, database: this, dispatch: this.dispatch, queryId, view };

    let query = new QueryView(params);

    if(once) {
      return query;
    }

    this.queries[queryId] = query;

    //Bind `cancel` event on query instance.
    query.on('cancel', this._removeQueryReference);
    return query;
  }

  /**
   * This method is used to Create, update or delete single/multiple documents.
   * 
   * - If you omit an `_id` parameter on a given document, the database will create a new document
   * - To update a document, you must include both an `_id` and `_rev` keys
   * - To delete a document, include a `_deleted` parameter with the value `true`
   * 
   * @param {*} docs - It can be single document or array of documents
   * @returns WriteDocsResponse promise
   * 
   * Promise rejects when operation is failed due to any error. Resolves otherwise.
   * 
   * NOTE: in case of multiple document, Promise resolves even if any one document is failed to update. rejects if 
   * all documents are failed to update/delete.
   * In case of any docs write failed, it wouldn't added in failedWrites.
   */
  save(docs) {
    if (!docs) {
      this.log.error(`${this.logPrefix}.save(): Invalid arguments : Docs are required`);
      return;
    }

    if (docs !== Object(docs) && !Array.isArray(docs)) {
      this.log.error(`${this.logPrefix}.save(): Invalid arguments : docs must be object or array`);
      return;
    }

    if (!Array.isArray(docs)) {
      docs = [docs];
    }

    if (!docs.length) {
      this.log.error(`${this.logPrefix}.save(): Invalid arguments : docs must have value`);
      return;
    }

    let writeDocsResponse = new WriteDocsResponse(this.couchdb.name, this.dbName, this.dispatch);
    this._performSave(docs, writeDocsResponse);
    return writeDocsResponse;
  }

  /**
   * This method is used to delete single/multiple documents.
   * 
   * Note: Documents which doesn't exist in the local database are created with `_deleted=true` 
   * (as very first revision). Ideally, this case won't happen because as a standard 
   * practice only documents which exists in the local database are deleted.
   * And if the document is already deleted then also a new revision of the same document is created
   * with `_deleted=true`. But, this time it effectively erases all other fields of the document.
   * 
   * @param {*} docIds - single or multiple docIds to be delete
   * @returns WriteDocsResponse promise
   */
  delete(docIds) {
    if (!docIds || (typeof docIds !== 'string' && !Array.isArray(docIds))) {
      this.log.error(`${this.logPrefix}.delete(): Invalid arguments : docIds must be valid string or Array of string`);
      return;
    }

    let writeDocsResponse = new WriteDocsResponse(this.couchdb.name, this.dbName, this.dispatch);
    this._delete(docIds, writeDocsResponse);
    return writeDocsResponse;
  }

  /**
   * This method reads documents from local db and prepare array of documents with `_daleted=true`.
   * @param {Array or String} docIds 
   * @param {WriteDocsResponse} writeDocsResponse 
   * @returns Promise
   */
  async _delete(docIds, writeDocsResponse) {
    try {
      let requestedAt = new Date().getTime();
      await this._fetchNotFoundDocsFromRemote(docIds);

      let docs = await this._fetchDocsFromLocalDB(docIds);
      //Put into array if single document object
      if (!Array.isArray(docs)) {
        docs = [docs];
      }

      let docsToBeDelete = [];
      let docsNotFound = [];
      let docsDeleted = [];

      docs.forEach(doc => {
        //Deleted doc
        if (doc.value && doc.value.deleted) {
          docsDeleted.push({
            id: doc.key,
            rev: doc.value.rev,
            _deleted: true
          });
          this.log.info(`_delete: document is already deleted. doc: `, doc);
          return;
        }

        //Not found case
        if (doc.error) {
          docsNotFound.push(doc.key);
          this.log.info(`_delete: document is not found. doc: `, doc);
          return;
        }

        docsToBeDelete.push({ ...doc.doc, _deleted: true });
      });

      if (docsNotFound.length === 1 && docs.length === 1) {
        writeDocsResponse._rejectLocalWrite({ error: 'document-not-found' });
        return;
      }

      if (docsToBeDelete.length) {
        this.log.debug(`${this.logPrefix}._delete(): Delete action requested for`, docsToBeDelete);
        return this._save(docsToBeDelete, writeDocsResponse);
      }

      if (docsDeleted.length) {
        let localResponse = docs.length === 1 ? docsDeleted[0] : docsDeleted;
        let remoteResponse = docs.length === 1 ? undefined : [];
        writeDocsResponse._resolveLocalWrite(localResponse, [], requestedAt);
        writeDocsResponse._resolveRemoteWrite(docs, remoteResponse);
        return;
      }

      if (docsNotFound.length) {
        writeDocsResponse._rejectLocalWrite({ error: 'document-not-found', docIds: docsNotFound });
        return;
      }

    } catch (err) {
      writeDocsResponse._rejectLocalWrite(err);
      this.log.error(`${this.logPrefix}._delete(): Delete action failed`, err);
    }
  }

  /**
   * @param {Array} docs Docs to be write
   * @param {*} writeDocsResponse 
   */
  async _save(docs, writeDocsResponse) {
    try {
      let requestedAt = new Date().getTime();
      let result = await this._saveLocal(docs, requestedAt, writeDocsResponse);
      await this._saveRemote(result, requestedAt, writeDocsResponse);
    }
    catch (err) {
      this.log.error(`${this.logPrefix}._save(): Write operation failed.`, err);
    }
  }

  /**
   * It parses successDocs and failedDocs from response
   * .
   * @param {Array} Array of documents.
   * @returns {Object} {successDocs, failedDocs}
   */
  _parseLocalSaveResponse(results) {
    let successDocs = [];
    let failedDocs = [];

    results.forEach((doc) => {
      if (doc.error) {
        failedDocs.push(doc);
        return;
      }

      successDocs.push({ id: doc.id, rev: doc.rev });
    });

    return {
      successDocs,
      failedDocs
    };
  }

  /**
   * It saves documents in local database.
   * It will resolve promise if any one of document write successfully in local database
   * It will reject promise if all document write failed.
   * @param {Array or String} docs
   * @param {TimeStamp} requestedAt
   * @param {WriteDocsResponse} writeDocsResponse
   * @returns Promise
   */
  async _saveLocal(docs, requestedAt, writeDocsResponse) {
    return new Promise(async (resolve, reject) => {
      let successDocs = [], failedDocs = [];
      let singleDoc = docs.length === 1 ? true : false;

      try {
        let results = await this.localDb.save(docs);

        if (!results) {
          return;
        }

        //Split successDocs and failedDocs
        let documents = this._parseLocalSaveResponse(results);
        successDocs = documents.successDocs;
        failedDocs = documents.failedDocs;

        //Rejects promise if not single document write successfully
        if (!successDocs.length) {
          if(singleDoc) {
            writeDocsResponse._rejectLocalWrite(results[0], docs, failedDocs);
            reject(results);
            return;
          }
          
          writeDocsResponse._resolveLocalWrite(results, docs, failedDocs);
          reject(results);
          return;
        }

        //It fetches all success documents with its revision history
        let successDocsWithRevs = await this.localDb.fetchWithRevs(successDocs);

        results = singleDoc ? results[0] : results;
        writeDocsResponse._resolveLocalWrite(results, successDocsWithRevs, requestedAt);
        this.log.info(`${this.logPrefix}._saveLocal(): Docs saved in localDB.`, successDocs);
        resolve(successDocsWithRevs);

      } catch (err) {
        writeDocsResponse._rejectLocalWrite(err, docs, failedDocs);
        this.log.error(`${this.logPrefix}._saveLocal(): Local document write failed.`, err);
        reject(err);
      }
    });
  }

  /**
   * This method used by _save method internally.
   * It's saves documents in remote database.
   * @param {Array} docs
   * @param {TimeStamp} requestedAt
   * @param {WriteDocsResponse} writeDocsResponse
   * @returns Promise
   */
  async _saveRemote(docs, requestedAt, writeDocsResponse) {
    return new Promise(async (resolve, reject) => {
      try {
        let remoteResponse = await this.remoteDb.save(docs, requestedAt);

        if(docs.length === 1 && remoteResponse.response[0]) {
          writeDocsResponse._rejectRemoteWrite(docs, { error: remoteResponse.response[0] });
          reject(remoteResponse.response[0]);
          return;
        }

        let response = docs.length === 1 ? undefined : remoteResponse.response;
        writeDocsResponse._resolveRemoteWrite(docs, response);
        this.log.info(`${this.logPrefix}._saveRemote(): Docs saved on remoteDB.`, remoteResponse);
        resolve(remoteResponse);

      } catch (err) {
        writeDocsResponse._rejectRemoteWrite(docs, err);
        this.log.error(`${this.logPrefix}._saveRemove(): Remote write failed.`, err);
        reject(err);
      }
    });
  }

  /**
   * It fetches documents from remote database which are not available in the local database and requested for 
   * update. It will save requested documents only after remote read is completed.
   * @param {Array} docs 
   * @param {WriteDocsResponse} writeDocsResponse 
   */
  async _performSave(docs, writeDocsResponse) {
    let docIds = this._getDocIds(docs);

    if (docIds.length) {
      await this._fetchNotFoundDocsFromRemote(docIds);
    }

    this._save(docs, writeDocsResponse);
  }

  /**
   * It filters given docs and returns array of docIds which are requested for update.
   * @param {Array} docs array of documents
   * @return {Array} array of docIds
   */
  _getDocIds(docs) {
    let updateRequestedDocs = docs.filter(doc => doc._id && doc._rev);
    return updateRequestedDocs.map(doc => doc._id);
  }

  /**
   * It fetches documents from remote database which are not available in local database from given docIds.
   * @param {Array} docIds
   * @return {Promise} which is resolved when requested documents are fetched from remote.
   */
  async _fetchNotFoundDocsFromRemote(docIds) {
    let notFoundDocIds = await this._getNotFoundDocIds(docIds);

    if (notFoundDocIds.length) {
      await this._fetchDocsFromRemote(notFoundDocIds);
    }
  }

  /**
   * It fetches documents from local database for given docIds and returns an array of docIds which are not found from local database.
   * @param {Array} docIds 
   * @return {Array} array of docIds which are not found from local database
   */
  async _getNotFoundDocIds(docIds) {
    let localDocs = await this._fetchDocsFromLocalDB(docIds);

    if (!Array.isArray(localDocs)) {
      localDocs = [localDocs];
    }

    let notFoundDoc = localDocs.filter(doc => doc.error);
    return notFoundDoc.map(doc => doc.key);
  }

  /**
   * It fetches documents from local database for given docIds.
   * @param {Array} docIds 
   * @returns {Promise} return promise which is resolved when documents are fetched from local database.
   */
  async _fetchDocsFromLocalDB(docIds) {
    return await this.localDb.fetch(docIds);
  }

  /**
   * It fetches documents from remote database for given docIds.
   * @param {Array} docIds 
   * @returns {Promise} return promise which is resolved when documents are fetched from remote database.
   */
  async _fetchDocsFromRemote(docIds) {
    return await this.getByIds({ docIds, once: true }).val();
  }

}
